<script>
    import { fade } from 'svelte/transition';
    import { createEventDispatcher, onDestroy } from 'svelte';
    
    const dispatch = createEventDispatcher();
    
    // Props
    export let show = false;
    export let message = '';
    export let variant = 'neutral'; // 'spec' | 'terms' | 'error' | 'neutral'
    export let position = 'bottom'; // 'bottom' | 'top' | 'left' | 'right'
    export let duration = 3000; // Auto-hide duration in ms, 0 = no auto-hide
    
    // Auto-hide logic
    let hideTimer;
    
    $: if (show && duration > 0) {
        // Clear any existing timer
        if (hideTimer) {
            clearTimeout(hideTimer);
        }
        
        // Set new timer
        hideTimer = setTimeout(() => {
            show = false;
            dispatch('hide');
        }, duration);
    } else if (!show && hideTimer) {
        // Clear timer if bubble is hidden
        clearTimeout(hideTimer);
        hideTimer = null;
    }
    
    // Position classes
    const positionClasses = {
        bottom: 'bubble-bottom',
        top: 'bubble-top',
        left: 'bubble-left',
        right: 'bubble-right'
    };
    
    // Cleanup on component destroy
    onDestroy(() => {
        if (hideTimer) {
            clearTimeout(hideTimer);
        }
    });
</script>

{#if show && message}
    <div 
        class="notification-bubble bubble-variant-{variant} {positionClasses[position]}"
        data-testid="notification-{variant}"
        transition:fade={{ duration: 200 }}
        role={variant === 'error' ? 'alert' : 'tooltip'}
        aria-live="polite"
    >
        {message}
    </div>
{/if}

<style>
.notification-bubble {
    position: absolute;
    z-index: var(--z-index-notification);
    padding: var(--spacing-2) var(--spacing-4);
    font-size: var(--font-size-sm);
    white-space: nowrap;
    border-radius: var(--border-radius-md);
    box-shadow: var(--shadow-md);
    pointer-events: none; /* Prevent blocking interactions */
}

/* Position variants */
.bubble-bottom {
    top: 100%;
    left: 50%;
    margin-top: var(--spacing-2);
    transform: translateX(-50%);
}

.bubble-top {
    bottom: 100%;
    left: 50%;
    margin-bottom: var(--spacing-2);
    transform: translateX(-50%);
}

.bubble-left {
    top: 50%;
    right: 100%;
    margin-right: var(--spacing-2);
    transform: translateY(-50%);
}

.bubble-right {
    top: 50%;
    left: 100%;
    margin-left: var(--spacing-2);
    transform: translateY(-50%);
}

/* Arrow pseudo-element */
.notification-bubble::before {
    position: absolute;
    content: '';
    border-style: solid;
}

/* Bottom arrow */
.bubble-bottom::before {
    top: -6px;
    left: 50%;
    border-width: 0 6px 6px;
    border-color: transparent;
    transform: translateX(-50%);
}

/* Top arrow */
.bubble-top::before {
    bottom: -6px;
    left: 50%;
    border-width: 6px 6px 0;
    border-color: transparent;
    transform: translateX(-50%);
}

/* Left arrow */
.bubble-left::before {
    top: 50%;
    right: -6px;
    border-width: 6px 0 6px 6px;
    border-color: transparent;
    transform: translateY(-50%);
}

/* Right arrow */
.bubble-right::before {
    top: 50%;
    left: -6px;
    border-width: 6px 6px 6px 0;
    border-color: transparent;
    transform: translateY(-50%);
}

/* Color variants */
.bubble-variant-spec {
    color: var(--color-bubble-text-spec);
    background-color: var(--color-bubble-bg-spec);
}

.bubble-variant-spec.bubble-bottom::before {
    border-bottom-color: var(--color-bubble-arrow-spec);
}

.bubble-variant-spec.bubble-top::before {
    border-top-color: var(--color-bubble-arrow-spec);
}

.bubble-variant-spec.bubble-left::before {
    border-left-color: var(--color-bubble-arrow-spec);
}

.bubble-variant-spec.bubble-right::before {
    border-right-color: var(--color-bubble-arrow-spec);
}

.bubble-variant-terms {
    z-index: 801;
    color: var(--color-bubble-text-terms);
    background-color: var(--color-bubble-bg-terms);
}

.bubble-variant-terms.bubble-bottom::before {
    border-bottom-color: var(--color-bubble-arrow-terms);
}

.bubble-variant-terms.bubble-top::before {
    border-top-color: var(--color-bubble-arrow-terms);
}

.bubble-variant-terms.bubble-left::before {
    border-left-color: var(--color-bubble-arrow-terms);
}

.bubble-variant-terms.bubble-right::before {
    border-right-color: var(--color-bubble-arrow-terms);
}

.bubble-variant-error {
    color: var(--color-bubble-text-error);
    background-color: var(--color-bubble-bg-error);
}

.bubble-variant-error.bubble-bottom::before {
    border-bottom-color: var(--color-bubble-arrow-error);
}

.bubble-variant-error.bubble-top::before {
    border-top-color: var(--color-bubble-arrow-error);
}

.bubble-variant-error.bubble-left::before {
    border-left-color: var(--color-bubble-arrow-error);
}

.bubble-variant-error.bubble-right::before {
    border-right-color: var(--color-bubble-arrow-error);
}

.bubble-variant-neutral {
    color: var(--color-bubble-text-neutral);
    background-color: var(--color-bubble-bg-neutral);
}

.bubble-variant-neutral.bubble-bottom::before {
    border-bottom-color: var(--color-bubble-arrow-neutral);
}

.bubble-variant-neutral.bubble-top::before {
    border-top-color: var(--color-bubble-arrow-neutral);
}

.bubble-variant-neutral.bubble-left::before {
    border-left-color: var(--color-bubble-arrow-neutral);
}

.bubble-variant-neutral.bubble-right::before {
    border-right-color: var(--color-bubble-arrow-neutral);
}

.bubble-variant-success {
    color: var(--color-bubble-text-success);
    background-color: var(--color-bubble-bg-success);
}

.bubble-variant-success.bubble-bottom::before {
    border-bottom-color: var(--color-bubble-arrow-success);
}

.bubble-variant-success.bubble-top::before {
    border-top-color: var(--color-bubble-arrow-success);
}

.bubble-variant-success.bubble-left::before {
    border-left-color: var(--color-bubble-arrow-success);
}

.bubble-variant-success.bubble-right::before {
    border-right-color: var(--color-bubble-arrow-success);
}

/* Responsive adjustments */
@media (width <= 768px) {
    .notification-bubble {
        max-width: 90vw;
        white-space: normal;
        word-wrap: break-word;
    }
}
</style>
